/**
 * Turns the served sections into the tab / dropdown tree the nav renders.
 *
 * Everything here reads `Section.nav`, a breadcrumb the backend builds — level 0
 * is a tab, deeper levels are dropdowns. Nothing in the frontend knows what a P&R
 * or a coverage is, so adding a breakdown is a backend registry entry and no
 * change here.
 *
 *     ["Advanced"]                                        tab only
 *     ["Turnovers", "Drive"]                              tab + one dropdown
 *     ["Offensive Actions", "P&R Ballhandler", "Coverage"] tab + two dropdowns
 */

import { PlayerStatSection } from "../../../models/PlayerStats";

export interface NavOption {
  label: string;
  /** A section reachable by choosing this option — what selecting it navigates to. */
  sectionKey: string;
}

export interface NavState {
  /** Level-0 tabs, in served order. */
  tabs: NavOption[];
  /** Level-1 options under the active tab. Empty when the tab has no drill-downs. */
  subs: NavOption[];
  /** Level-2 options under the active tab + sub. Empty when there are none. */
  views: NavOption[];
  activeTab: string | null;
  activeSub: string | null;
  activeView: string | null;
}

/** First section whose nav starts with `prefix`. */
function firstUnder(
  sections: PlayerStatSection[],
  prefix: string[],
): PlayerStatSection | undefined {
  return sections.find((s) => prefix.every((p, i) => s.nav[i] === p));
}

/** Distinct labels at `level` among sections matching `prefix`, in served order. */
function optionsAt(
  sections: PlayerStatSection[],
  prefix: string[],
  level: number,
): NavOption[] {
  const seen = new Map<string, string>();
  sections.forEach((s) => {
    if (!prefix.every((p, i) => s.nav[i] === p)) return;
    const label = s.nav[level];
    if (label === undefined || seen.has(label)) return;
    // The first section under a label is where choosing it lands you — which is
    // why the backend orders Overview ahead of the drill-downs.
    seen.set(label, firstUnder(sections, [...prefix, label])?.key ?? s.key);
  });
  return Array.from(seen, ([label, sectionKey]) => ({ label, sectionKey }));
}

export function buildNav(
  sections: PlayerStatSection[],
  active: PlayerStatSection | null,
): NavState {
  const nav = active?.nav ?? [];
  const [tab = null, sub = null, view = null] = nav;
  return {
    tabs: optionsAt(sections, [], 0),
    // A single option is not a choice — the nav hides these, so an action with
    // one breakdown reads as a plain sub-tab rather than a dropdown that does
    // nothing. They fill in as more breakdowns land.
    subs: tab ? optionsAt(sections, [tab], 1) : [],
    views: tab && sub ? optionsAt(sections, [tab, sub], 2) : [],
    activeTab: tab,
    activeSub: sub,
    activeView: view,
  };
}
